/**
 * The decisions the Studio welcome makes before it draws: how the trainer's name breaks and how big
 * it is set, how long the survey is said to take.
 *
 * ── The name is set as large as the frame allows ─────────────────────────────────────────────
 * On a phone the first word stands on one line and the rest on the next (`V3-Tel-Powitanie`); on a
 * desktop the whole name is one line along the bottom (`V3-Desk-Powitanie`). Either way the size is
 * the smaller of the face's cap and what fits the longest line across the frame — computed from the
 * face's average letter width, so a condensed face sets the same name larger than a wide one. The
 * answer is a CSS length in container units, so a scaled preview frame sizes its name exactly as a
 * phone of that width would.
 */

import { displayInitials } from '~/shared/lib/displayInitials';

import { headingFont, type HeadingFontId } from './catalog';

export interface NameLines {
  readonly first: string;
  readonly rest: string;
}

/** First word, then everything after it. A one-word name leaves `rest` empty. */
export function nameLines(displayName: string): NameLines {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  return { first: words[0] ?? '', rest: words.slice(1).join(' ') };
}

/**
 * The phone's frame is 390 wide and the mockup lets the name run 352 of it; a face narrower than
 * this letter width may grow past the wide faces' cap, as Anton and Bebas Neue do in the mockup.
 */
const PHONE_MEASURE = 352 / 390;
const PHONE_CAP_PX = 118;
const PHONE_CONDENSED_CAP_PX = 150;
const CONDENSED_LETTER_WIDTH = 0.55;

/** The desktop sets the name at 236px in Bricolage and lets it run the frame less both gutters. */
const DESKTOP_CAP_PX = 236;
const DESKTOP_REFERENCE_WIDTH = 0.7;
const DESKTOP_GUTTERS_PX = 72;

/**
 * The phone's name size: a CSS length, container-relative.
 *
 * `scale` is the heading size the trainer chose (`headingScale`). It moves the cap; the fit across
 * the frame only ever comes down with it, so a larger size grows a short name and leaves a long one
 * exactly as wide as the frame allows. At 1 the length is the template's own.
 */
export function phoneNameSize(displayName: string, heading: HeadingFontId, scale = 1): string {
  const face = headingFont(heading);
  const { first, rest } = nameLines(displayName);
  const longest = Math.max([...first].length, [...rest].length, 1);
  const cap = face.letterWidth < CONDENSED_LETTER_WIDTH ? PHONE_CONDENSED_CAP_PX : PHONE_CAP_PX;
  const fit = ((PHONE_MEASURE * 100) / (longest * face.letterWidth)) * Math.min(scale, 1);
  return `min(${pixels(cap * scale)}px, ${fit.toFixed(3)}cqi)`;
}

/** The desktop's one-line name size: a CSS length, container-relative. `scale` as on the phone. */
export function desktopNameSize(displayName: string, heading: HeadingFontId, scale = 1): string {
  const face = headingFont(heading);
  const letters = Math.max([...displayName.trim()].length, 1);
  const cap = Math.floor((DESKTOP_CAP_PX * DESKTOP_REFERENCE_WIDTH) / face.letterWidth);
  const across = (letters * face.letterWidth) / Math.min(scale, 1);
  return `min(${pixels(cap * scale)}px, calc((100cqi - ${String(DESKTOP_GUTTERS_PX)}px) / ${across.toFixed(3)}))`;
}

/** A pixel count to at most two decimals, a whole one without any. */
function pixels(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/**
 * How long the survey is said to take. Half a minute a question, never under one — twelve
 * questions is „około 6 min" in the mockup.
 */
export function estimatedMinutes(questionCount: number): number {
  return Math.max(1, Math.round(Math.max(0, questionCount) / 2));
}

/** The strip under the name holds at most this many lines. */
export const STRIP_MAX_LINES = 3;

/**
 * The strip's lines, each drawn as its own row exactly as typed — repeated spaces included. A
 * document with more lines than the strip holds (a newer or a foreign writer) runs the overflow
 * into the last row rather than losing it. Blank is no rows at all.
 */
export function stripLines(line: string): readonly string[] {
  if (line.trim() === '') return [];
  const lines = line.split(/\r\n?|\n/);
  return lines.length <= STRIP_MAX_LINES
    ? lines
    : [...lines.slice(0, STRIP_MAX_LINES - 1), lines.slice(STRIP_MAX_LINES - 1).join(' ')];
}

/** The two letters the logo tile shows when the trainer has no logo of their own. */
export function monogram(displayName: string): string {
  return displayInitials(displayName);
}

/** The name the header's brand line shows: the brand name, else the trainer's own. */
export function brandLineName(texts: { readonly brandName: string; readonly displayName: string }): string {
  return texts.brandName.trim() !== '' ? texts.brandName.trim() : texts.displayName.trim();
}

/** The name the copy calls the trainer by: their signature, else the first word of their name. */
export function trainerCallName(signature: string, displayName: string): string {
  return signature.trim() !== '' ? signature.trim() : nameLines(displayName).first;
}
